import { useState, useCallback } from 'react';

export type TimelinePeriod = 'all' | 'past' | 'future' | 'thisYear';
export type TimelineEventType = 'milestone' | 'deadline' | 'hearing';
export type TimelineVerification = 'confirmed' | 'unconfirmed';

export interface TimelineFilterState {
  period: TimelinePeriod;
  types: TimelineEventType[];      // empty array = all (no filter)
  verification: TimelineVerification[]; // empty array = all (no filter)
}

const DEFAULT_TIMELINE_FILTER: TimelineFilterState = {
  period: 'all',
  types: [],
  verification: [],
};

export function useTimelineFilterState() {
  const [tlFilters, setTlFilters] = useState<TimelineFilterState>(DEFAULT_TIMELINE_FILTER);

  const setPeriod = useCallback((period: TimelinePeriod) => {
    setTlFilters((f) => ({ ...f, period }));
  }, []);

  const toggleTimelineType = useCallback((type: TimelineEventType) => {
    setTlFilters((f) => ({
      ...f,
      types: f.types.includes(type)
        ? f.types.filter((t) => t !== type)
        : [...f.types, type],
    }));
  }, []);

  const toggleVerification = useCallback((v: TimelineVerification) => {
    setTlFilters((f) => ({
      ...f,
      verification: f.verification.includes(v)
        ? f.verification.filter((x) => x !== v)
        : [...f.verification, v],
    }));
  }, []);

  const clearTimelineFilters = useCallback(() => {
    setTlFilters(DEFAULT_TIMELINE_FILTER);
  }, []);

  const isTimelineFilterActive =
    tlFilters.period !== 'all' ||
    tlFilters.types.length > 0 ||
    tlFilters.verification.length > 0;

  return {
    tlFilters,
    isTimelineFilterActive,
    setPeriod,
    toggleTimelineType,
    toggleVerification,
    clearTimelineFilters,
  };
}
